import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  lcTimeslotClosureApi,
  Timeslot,
  TimeslotClosure,
  TimeslotClosureState,
} from '@/api/lc'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import { Pause, Pencil, X } from 'lucide-react'

interface Props {
  timeslot: Timeslot
  onClose: () => void
}

const stateBadgeClass: Record<TimeslotClosureState, string> = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  ONGOING: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-gray-100 text-gray-500',
}

/**
 * 클래스(timeslot) 휴강 관리 모달 — P2.
 * 목록(조회일 기준 상태배지 + 게이팅 액션) + 추가/수정 폼. 휴강 ≠ 휴원(원생) — 클래스 일정 전용.
 */
export default function ClosureModal({ timeslot, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [periodError, setPeriodError] = useState<string | null>(null)

  const { data: closuresData } = useQuery({
    queryKey: ['lcTimeslotClosures', timeslot.timeslotId],
    queryFn: () => lcTimeslotClosureApi.list(timeslot.timeslotId),
  })
  const closures = closuresData?.data?.data || []

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lcTimeslotClosures', timeslot.timeslotId] })
    queryClient.invalidateQueries({ queryKey: ['lcTimeslotClosures'] })
    queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
  }

  const resetForm = () => {
    setEditingId(null)
    setStartDate('')
    setEndDate('')
    setReason('')
    setPeriodError(null)
  }

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })
      ?.response?.data?.error?.code
    return code ? t(`apiError.${code}`, { ns: 'common', defaultValue: fallback }) : fallback
  }

  const createMutation = useMutation({
    mutationFn: () =>
      lcTimeslotClosureApi.create({
        timeslotId: timeslot.timeslotId,
        startDate,
        endDate,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      invalidate()
      resetForm()
      toast.success(t('timeslot.closure.toast.createSuccess'))
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('timeslot.closure.toast.createFailed'))),
  })

  const updateMutation = useMutation({
    mutationFn: (id: number) =>
      lcTimeslotClosureApi.update(id, { startDate, endDate, reason: reason.trim() }),
    onSuccess: () => {
      invalidate()
      resetForm()
      toast.success(t('timeslot.closure.toast.updateSuccess'))
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('timeslot.closure.toast.updateFailed'))),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => lcTimeslotClosureApi.delete(id),
    onSuccess: () => {
      invalidate()
      toast.success(t('timeslot.closure.toast.cancelSuccess'))
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('timeslot.closure.toast.cancelFailed'))),
  })

  // 클라 검증: 시작/종료일/사유 필수 + 종료일 ≥ 시작일. (BE 에러코드도 토스트 매핑되나 1차 차단.)
  const validate = (): boolean => {
    if (!startDate) {
      setPeriodError(t('timeslot.closure.validation.startRequired'))
      return false
    }
    if (!endDate) {
      setPeriodError(t('timeslot.closure.validation.endRequired'))
      return false
    }
    if (!reason.trim()) {
      setPeriodError(t('timeslot.closure.validation.reasonRequired'))
      return false
    }
    if (startDate && endDate < startDate) {
      setPeriodError(t('timeslot.closure.validation.endBeforeStart'))
      return false
    }
    setPeriodError(null)
    return true
  }

  const handleSubmit = () => {
    if (!validate()) return
    if (editingId != null) {
      updateMutation.mutate(editingId)
    } else {
      createMutation.mutate()
    }
  }

  const startEdit = (closure: TimeslotClosure) => {
    setEditingId(closure.closureId)
    setStartDate(closure.startDate)
    setEndDate(closure.endDate)
    setReason(closure.reason || '')
    setPeriodError(null)
  }

  const handleCancel = (closure: TimeslotClosure) => {
    if (confirm(t('timeslot.closure.confirm.cancel'))) {
      deleteMutation.mutate(closure.closureId)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="w-4 h-4" />
            {t('timeslot.closure.title', { name: timeslot.name })}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500">{t('timeslot.closure.description')}</p>

        {/* 휴강 목록 */}
        <ul className="mt-3 space-y-2">
          {closures.length === 0 ? (
            <li className="text-sm text-gray-400 py-4 text-center">{t('timeslot.closure.empty')}</li>
          ) : (
            closures.map((c) => (
              <li
                key={c.closureId}
                data-testid={`closure-row-${c.closureId}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${stateBadgeClass[c.state]}`}>
                      {t(`timeslot.closure.state.${c.state}`)}
                    </span>
                    <span className="text-sm text-gray-700">
                      {t('timeslot.closure.period', { start: c.startDate, end: c.endDate })}
                    </span>
                  </div>
                  {c.reason && <p className="mt-0.5 text-xs text-gray-400 truncate">{c.reason}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {/* 게이팅: SCHEDULED→[수정][취소], ONGOING→[수정], ENDED→읽기전용 */}
                  {(c.state === 'SCHEDULED' || c.state === 'ONGOING') && (
                    <button
                      type="button"
                      onClick={() => startEdit(c)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <Pencil className="w-3 h-3" />
                      {t('timeslot.closure.actions.edit')}
                    </button>
                  )}
                  {c.state === 'SCHEDULED' && (
                    <button
                      type="button"
                      onClick={() => handleCancel(c)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-3 h-3" />
                      {t('timeslot.closure.actions.cancel')}
                    </button>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>

        {/* 추가/수정 폼 */}
        <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('timeslot.closure.form.startDate')}
              </label>
              <input
                type="date"
                data-testid="closure-start"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('timeslot.closure.form.endDate')}
              </label>
              <input
                type="date"
                data-testid="closure-end"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('timeslot.closure.form.reason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('timeslot.closure.form.reasonPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {periodError && <p className="text-xs text-red-600">{periodError}</p>}
          <div className="flex justify-end gap-2">
            {editingId != null && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t('button.cancel', { ns: 'common' })}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {editingId != null ? t('timeslot.closure.actions.edit') : t('timeslot.closure.form.submit')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
